/**
 * Journey Event Service
 *
 * Central event emission SDK for the Journey Intelligence System.
 * Provides structured event tracking with canonical schema, batching,
 * and automatic session/journey correlation.
 *
 * Usage:
 *   import { journeyEvents } from '../services/journeyEventService';
 *
 *   // Start tracking a journey
 *   const tracker = journeyEvents.startJourney('J1_PROVIDER_REGISTRATION', 'registration_page');
 *
 *   // Track steps
 *   tracker.enterStep('npi_search', 1);
 *   tracker.completeStep('npi_search', { npiType: 'CRNA' });
 *   tracker.failStep('sms_verification', 'INVALID_CODE', 'Code expired');
 *
 *   // Track milestones
 *   tracker.milestone('M_PROVIDER_NPI_VERIFIED');
 *
 *   // Complete/abandon journey
 *   tracker.complete({ matchCount: 142 });
 *   tracker.abandon();
 */

import { getBackendUrl } from '../utils/apiUrl';
import { getOrCreateJourneyAnonymousId } from '../utils/journeyIdentity';
import { getProviderLandingContext, markProviderLandingAccountCreated } from '../utils/providerLandingExperiment';

const BACKEND_URL = getBackendUrl();
const BATCH_INTERVAL_MS = 3000; // Flush every 3 seconds
const MAX_BATCH_SIZE = 50; // Flush when batch reaches this size
const SESSION_KEY = 'rxr_journey_session_id';

// ── Session & Identity ────────────────────────────────────────────────────────

function getOrCreateSessionId() {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

function generateJourneyId() {
  return `j_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

// ── Device Detection ──────────────────────────────────────────────────────────

function detectDeviceType() {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
}

function detectPlatform() {
  if (window.isNativeApp) {
    return /iphone|ipad|ipod/i.test(navigator.userAgent) ? 'ios' : 'android';
  }
  return 'web';
}

// ── Source Channel Detection ──────────────────────────────────────────────────

function detectSourceChannel() {
  try {
    const utmRaw = sessionStorage.getItem('rxr_utm');
    if (utmRaw) {
      const utm = JSON.parse(utmRaw);
      if (utm.medium === 'cpc' || utm.medium === 'paid') return 'paid';
      if (utm.medium === 'social') return 'social';
      if (utm.medium === 'email') return 'email';
      if (utm.source === 'referral') return 'referral';
    }
  } catch { /* ignore */ }

  if (document.referrer) {
    const ref = new URL(document.referrer).hostname;
    if (ref.includes('google') || ref.includes('bing') || ref.includes('yahoo')) return 'organic';
    if (ref.includes('facebook') || ref.includes('instagram') || ref.includes('linkedin')) return 'social';
    if (!ref.includes('rxrooster')) return 'referral';
  }

  return 'direct';
}

function getTrafficSource() {
  try {
    const utmRaw = sessionStorage.getItem('rxr_utm');
    if (utmRaw) {
      const utm = JSON.parse(utmRaw);
      return utm.source || null;
    }
  } catch { /* ignore */ }
  return null;
}

// ── Event Queue & Batching ────────────────────────────────────────────────────

let eventQueue = [];
let flushTimer = null;
let authToken = null;
let userId = null;
let persona = null;

function enqueueEvent(event) {
  const isProviderFunnel = event.journeyType === 'J1_PROVIDER_REGISTRATION' ||
    event.screenId === 'provider_service_landing' || event.screenId === 'provider_schedule' ||
    event.screenId === 'providers_map';
  const registered = event.journeyType === 'J1_PROVIDER_REGISTRATION' &&
    event.eventName === 'milestone.reached' && event.metadata?.milestoneId === 'M_PROVIDER_REGISTERED';
  const landing = isProviderFunnel ? (
    registered ? markProviderLandingAccountCreated() : getProviderLandingContext()
  ) : null;
  try {
    eventQueue.push({
      ...event,
      ...(landing ? {
        experimentVariant: landing.experimentVariant || undefined,
        metadata: { ...event.metadata, providerLanding: landing },
      } : {}),
      sessionId: getOrCreateSessionId(),
      anonymousId: getOrCreateJourneyAnonymousId(),
      userId: userId || undefined,
      timestamp: new Date().toISOString(),
      deviceType: detectDeviceType(),
      platform: detectPlatform(),
      sourceChannel: detectSourceChannel(),
      trafficSource: getTrafficSource(),
      persona: persona || undefined,
      releaseVersion: process.env.REACT_APP_VERSION || undefined,
    });
  } catch {
    // Analytics storage can be disabled. Tracking must never block a CTA or
    // successful registration; do not invent a different experiment identity.
    return;
  }

  if (eventQueue.length >= MAX_BATCH_SIZE) {
    flushEvents();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flushEvents, BATCH_INTERVAL_MS);
  }
}

async function flushEvents() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  if (eventQueue.length === 0) return;

  const batch = [...eventQueue];
  eventQueue = [];

  const endpoint = authToken
    ? `${BACKEND_URL}/journey-events`
    : `${BACKEND_URL}/journey-events/anon`;

  const headers = {
    'Content-Type': 'application/json',
  };
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  try {
    // Use sendBeacon for page unload, fetch otherwise
    const payload = JSON.stringify({ events: batch });

    if (document.visibilityState === 'hidden' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(endpoint, blob);
    } else {
      fetch(endpoint, {
        method: 'POST',
        headers,
        body: payload,
        keepalive: true, // Survive page navigation
      }).catch(() => {
        // Silent failure — never disrupt user experience
      });
    }
  } catch {
    // Silent failure
  }
}

// Flush on page hide/unload
if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushEvents();
    }
  });
  window.addEventListener('pagehide', flushEvents);
}

// ── Journey Tracker ───────────────────────────────────────────────────────────

class JourneyTracker {
  constructor(journeyType, screenId, journeyId) {
    this.journeyType = journeyType;
    this.journeyId = journeyId || generateJourneyId();
    this.screenId = screenId;
    this.currentStep = null;
    this.stepStartTime = null;
    this.isCompleted = false;
    this.isAbandoned = false;

    // Emit journey.started
    enqueueEvent({
      journeyId: this.journeyId,
      journeyType: this.journeyType,
      stepId: 'entry',
      screenId: this.screenId,
      pagePath: window.location.pathname,
      eventName: 'journey.started',
      eventType: 'lifecycle',
    });
  }

  enterStep(stepId, stepIndex, screenOverride) {
    // A transition is not evidence that the user abandoned the task.
    if (this.currentStep && this.stepStartTime) {
      enqueueEvent({
        journeyId: this.journeyId,
        journeyType: this.journeyType,
        stepId: this.currentStep,
        screenId: this.screenId,
        pagePath: window.location.pathname,
        eventName: 'step.exited',
        eventType: 'progression',
        metadata: { reason: 'step_transition', nextStep: stepId },
        timeSpentMs: Date.now() - this.stepStartTime,
      });
    }

    this.currentStep = stepId;
    this.stepStartTime = Date.now();

    enqueueEvent({
      journeyId: this.journeyId,
      journeyType: this.journeyType,
      stepId,
      stepIndex,
      screenId: screenOverride || this.screenId,
      pagePath: window.location.pathname,
      eventName: 'step.entered',
      eventType: 'progression',
    });
  }

  completeStep(stepId, metadata) {
    const timeSpent = this.stepStartTime ? Date.now() - this.stepStartTime : undefined;

    enqueueEvent({
      journeyId: this.journeyId,
      journeyType: this.journeyType,
      stepId: stepId || this.currentStep,
      screenId: this.screenId,
      pagePath: window.location.pathname,
      eventName: 'step.completed',
      eventType: 'progression',
      result: 'success',
      timeSpentMs: timeSpent,
      metadata,
    });

    if (stepId === this.currentStep || !stepId) {
      this.currentStep = null;
      this.stepStartTime = null;
    }
  }

  skipStep(stepId, metadata) {
    enqueueEvent({
      journeyId: this.journeyId,
      journeyType: this.journeyType,
      stepId: stepId || this.currentStep,
      screenId: this.screenId,
      pagePath: window.location.pathname,
      eventName: 'step.skipped',
      eventType: 'progression',
      result: 'skip',
      metadata,
    });

    if (stepId === this.currentStep || !stepId) {
      this.currentStep = null;
      this.stepStartTime = null;
    }
  }

  failStep(stepId, errorCode, errorMessage, metadata) {
    enqueueEvent({
      journeyId: this.journeyId,
      journeyType: this.journeyType,
      stepId: stepId || this.currentStep,
      screenId: this.screenId,
      pagePath: window.location.pathname,
      eventName: 'step.failed',
      eventType: 'error',
      result: 'failure',
      errorCode,
      errorMessage,
      metadata,
    });
  }

  milestone(milestoneId, metadata) {
    enqueueEvent({
      journeyId: this.journeyId,
      journeyType: this.journeyType,
      stepId: this.currentStep || 'unknown',
      screenId: this.screenId,
      pagePath: window.location.pathname,
      eventName: 'milestone.reached',
      eventType: 'achievement',
      metadata: { milestoneId, ...metadata },
    });
  }

  validationError(field, errorType, metadata) {
    enqueueEvent({
      journeyId: this.journeyId,
      journeyType: this.journeyType,
      stepId: this.currentStep || 'unknown',
      screenId: this.screenId,
      pagePath: window.location.pathname,
      eventName: 'validation.error',
      eventType: 'error',
      errorCode: errorType,
      metadata: { field, ...metadata },
    });
  }

  apiError(endpoint, statusCode, errorMessage) {
    enqueueEvent({
      journeyId: this.journeyId,
      journeyType: this.journeyType,
      stepId: this.currentStep || 'unknown',
      screenId: this.screenId,
      pagePath: window.location.pathname,
      eventName: 'api.error',
      eventType: 'error',
      errorCode: String(statusCode),
      errorMessage,
      metadata: { endpoint },
    });
  }

  click(elementId, metadata) {
    enqueueEvent({
      journeyId: this.journeyId,
      journeyType: this.journeyType,
      stepId: this.currentStep || 'unknown',
      screenId: this.screenId,
      pagePath: window.location.pathname,
      eventName: 'interaction.click',
      eventType: 'interaction',
      metadata: { elementId, ...metadata },
    });
  }

  complete(metadata) {
    if (this.isCompleted || this.isAbandoned) return;
    this.isCompleted = true;

    enqueueEvent({
      journeyId: this.journeyId,
      journeyType: this.journeyType,
      stepId: 'complete',
      screenId: this.screenId,
      pagePath: window.location.pathname,
      eventName: 'journey.completed',
      eventType: 'lifecycle',
      result: 'success',
      metadata,
    });

    // Flush immediately on completion (high-value event)
    flushEvents();
  }

  exit(metadata) {
    if (this.isCompleted || this.isAbandoned || this.isExited) return;
    this.isExited = true;
    enqueueEvent({
      journeyId: this.journeyId,
      journeyType: this.journeyType,
      stepId: this.currentStep || 'unknown',
      screenId: this.screenId,
      pagePath: window.location.pathname,
      eventName: 'journey.exited',
      eventType: 'lifecycle',
      metadata: { lastStep: this.currentStep, ...metadata },
    });
    flushEvents();
  }

  abandon(metadata) {
    if (this.isCompleted || this.isAbandoned) return;
    this.isAbandoned = true;

    const timeSpent = this.stepStartTime ? Date.now() - this.stepStartTime : undefined;

    enqueueEvent({
      journeyId: this.journeyId,
      journeyType: this.journeyType,
      stepId: this.currentStep || 'unknown',
      screenId: this.screenId,
      pagePath: window.location.pathname,
      eventName: 'journey.abandoned',
      eventType: 'lifecycle',
      result: 'abandon',
      timeSpentMs: timeSpent,
      metadata: { lastStep: this.currentStep, ...metadata },
    });

    // Flush immediately on abandonment (high-value event)
    flushEvents();
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

export const journeyEvents = {
  /**
   * Start tracking a new journey instance.
   * Returns a JourneyTracker with step/milestone/complete/abandon methods.
   */
  startJourney(journeyType, screenId, existingJourneyId) {
    return new JourneyTracker(journeyType, screenId, existingJourneyId);
  },

  /**
   * Set the authenticated user identity.
   * Call this after login/registration to enrich future events.
   */
  setUser(id, role) {
    userId = id;
    persona = role;
  },

  /**
   * Set the auth token for authenticated event submission.
   */
  setAuthToken(token) {
    authToken = token;
  },

  /**
   * Clear user identity (on logout).
   */
  clearUser() {
    userId = null;
    persona = null;
    authToken = null;
  },

  /**
   * Emit a standalone event (not part of a tracked journey).
   * Use sparingly — prefer journey-scoped events.
   */
  trackEvent(eventName, eventType, screenId, metadata) {
    enqueueEvent({
      journeyId: 'standalone',
      journeyType: 'standalone',
      stepId: 'standalone',
      screenId: screenId || window.location.pathname,
      pagePath: window.location.pathname,
      eventName,
      eventType,
      metadata,
    });
  },

  /**
   * Force flush all queued events (e.g., before navigation).
   */
  flush() {
    flushEvents();
  },

  /**
   * Get the current session ID.
   */
  getSessionId() {
    return getOrCreateSessionId();
  },

  /**
   * Get the current anonymous ID.
   */
  getAnonymousId() {
    return getOrCreateJourneyAnonymousId();
  },
};

export default journeyEvents;
